import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLocationContext } from '../contexts/LocationContext';
import { clockIn, clockOut, getActiveEntry, getBrowserCoords } from '../services/timeService';

const formatElapsed = (sec) => {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const ClockInOut = () => {
  const { membership, user } = useAuth();
  const { currentLocation } = useLocationContext();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState('');
  const tickRef = useRef(null);

  // Hide for owners (per requirement); also hide for superadmins (Oliver)
  // and when membership hasn't loaded yet.
  const role = membership?.role;
  const shouldHide = !membership || role === 'owner' || user?.is_superadmin;

  const fetchActive = async () => {
    try {
      const data = await getActiveEntry();
      setEntry(data.entry);
    } catch (err) {
      // Silent: location header may not be ready on first paint.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (shouldHide) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchActive();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocation?.id, shouldHide]);

  // Tick the elapsed timer once per second while an entry is open.
  useEffect(() => {
    if (!entry || entry.clock_out) {
      if (tickRef.current) clearInterval(tickRef.current);
      tickRef.current = null;
      setElapsed(0);
      return;
    }
    const start = new Date(entry.clock_in).getTime();
    const update = () => setElapsed(Math.max(0, Math.floor((Date.now() - start) / 1000)));
    update();
    tickRef.current = setInterval(update, 1000);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
      tickRef.current = null;
    };
  }, [entry]);

  if (shouldHide || loading) return null;

  const handleClockIn = async () => {
    setError('');
    setBusy(true);
    try {
      const coords = await getBrowserCoords();
      const e = await clockIn(coords ? {
        lat: coords.lat, lon: coords.lon, accuracy: coords.accuracy,
      } : {});
      setEntry(e);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to clock in');
    } finally {
      setBusy(false);
    }
  };

  const handleClockOut = async () => {
    if (!entry) return;
    setError('');
    setBusy(true);
    try {
      const coords = await getBrowserCoords();
      await clockOut(entry.id, coords ? {
        lat: coords.lat, lon: coords.lon, accuracy: coords.accuracy,
      } : {});
      setEntry(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to clock out');
    } finally {
      setBusy(false);
    }
  };

  const isOpen = entry && !entry.clock_out;

  return (
    <div className={`rounded-lg shadow p-4 flex items-center justify-between gap-3 ${isOpen ? 'bg-green-50 border border-green-200' : 'bg-white'}`}>
      <div className="min-w-0">
        <div className="text-sm font-medium text-gray-900">
          {isOpen ? 'You are clocked in' : 'Not clocked in'}
        </div>
        {isOpen ? (
          <div className="text-xs text-gray-600 mt-0.5">
            <span className="font-mono">{formatElapsed(elapsed)}</span>
            <span className="text-gray-400"> elapsed since {new Date(entry.clock_in).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
          </div>
        ) : (
          <div className="text-xs text-gray-500 mt-0.5">Tap to start your shift.</div>
        )}
        {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Link
          to="/timesheet"
          className="hidden sm:inline text-xs text-gray-500 hover:text-gray-700 underline"
        >
          Timesheet
        </Link>
        {isOpen ? (
          <button
            onClick={handleClockOut}
            disabled={busy}
            className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            {busy ? '...' : 'Clock out'}
          </button>
        ) : (
          <button
            onClick={handleClockIn}
            disabled={busy}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            {busy ? '...' : 'Clock in'}
          </button>
        )}
      </div>
    </div>
  );
};

export default ClockInOut;
